import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '../../../styled-components/components/Box';
import CalculationBox from '../../../styled-components/components/CalculationBox';
import { Column, Columns } from '../../../styled-components/components/Columns';
import Content from '../../../styled-components/components/Content';
import { H3 } from '../../../styled-components/components/Heading';
import { IconBulb, IconLightning } from '../../../styled-components/components/Icon';
import { IconBackdrop } from '../../../styled-components/components/IconBackdrop';
import InputLabel from '../../../styled-components/components/InputLabel';
import InputSelect from '../../../styled-components/components/InputSelect';
import { LinkButton } from '../../../styled-components/components/LinkButton';
import { Skeleton } from '../../../styled-components/components/Skeleton';
import { StackMedium, StackSmall, StackTiny } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';

import { bindActionCreators } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import type Item from '../../../@types/item';
import useEvCharchingCostPerKilometer from '../../../Data/EvCalculation/useEvCharchingCostPerKilometer';
import {
  trackChargingCostCustomizeClick as _trackChargingCostCustomizeClick,
  trackChargingCostParameterChange as _trackChargingCostParameterChange,
} from '../../../actions/tracking';
import RangeSliderSimple from '../../../components/RangeSlider/RangeSliderSimple';
import { numberSeparator } from '../../../helpers/formats';
import { prettify } from '../../../helpers/prices';
import { getMarket } from '../../../i18n';
import Alert from '../../../styled-components/components/Alert';
import ChargingCostModal from './ChargingCostModal';

const calculateCostPerMonth = (costPerKilometer: number | undefined, distance: number) => {
  if (!costPerKilometer) return undefined;
  return (costPerKilometer * distance * 10) / 12;
};

const DistanceOptions = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000];

interface ChargingCostProps {
  id: string;
  item: Item;
}

const ChargingCost = ({ id, item }: ChargingCostProps) => {
  const { t } = useTranslation(['common', 'glossary', 'item']);
  const dispatch = useDispatch();
  const trackChargingCostParameterChange = bindActionCreators(
    _trackChargingCostParameterChange,
    dispatch,
  );
  const trackChargingCostCustomizeClick = bindActionCreators(
    _trackChargingCostCustomizeClick,
    dispatch,
  );

  const [distance, setDistance] = useState(1500);

  const [data, request] = useEvCharchingCostPerKilometer(id);
  const [modal, setModal] = useState(0);
  const onCloseModal = useCallback(() => setModal(0), []);
  const [initialRequestSuccessful, setInitialRequestSuccessful] = useState<boolean | undefined>(
    !!data || undefined,
  );

  useEffect(() => {
    if (initialRequestSuccessful === undefined && !request.isLoading) {
      setInitialRequestSuccessful(request.isSuccess);
    }
  }, [request.isSuccess]);

  const costPerKilometer = data?.response.description.costPerKilometer.value;

  const costPerMonth = useMemo(
    () => calculateCostPerMonth(costPerKilometer, distance),
    [costPerKilometer, distance],
  );

  const costPerKwh = data?.response.description.costPerKwh;

  const onDistanceAfterChange = useCallback(
    (nextDistance: number) => {
      trackChargingCostParameterChange({
        item,
        information: {
          parameter: 'distance-slider',
          distance: `${nextDistance}`,
          context: 'inline',
          chargingCost: calculateCostPerMonth(costPerKilometer, nextDistance),
        },
      });
    },
    [costPerKilometer],
  );

  const onDistanceUpdate = useCallback((value: number) => setDistance(value), []);
  const onDistanceChangeSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const nextDistance = Number.parseInt(e.currentTarget.value, 10);
      trackChargingCostParameterChange({
        item,
        information: {
          parameter: 'distance-dropdown',
          distance: `${nextDistance}`,
          context: 'inline',
          chargingCost: calculateCostPerMonth(costPerKilometer, nextDistance),
        },
      });
      setDistance(nextDistance);
    },
    [costPerKilometer],
  );

  const onOpenModal = useCallback(
    (customPriceAsDefault = 1) => {
      trackChargingCostCustomizeClick({
        item,
        information: {
          label: customPriceAsDefault === 1 ? 'Visa detaljer' : 'Öppna uträknaren',
          distance: `${distance}`,
          context: 'inline',
          chargingCost: calculateCostPerMonth(costPerKilometer, distance),
        },
      });
      setModal(customPriceAsDefault);
    },
    [distance],
  );

  const marketCode = getMarket().marketCode;
  const mileageMultiplier = marketCode === 'NO' ? 10 : 1;

  if (!initialRequestSuccessful) return null;

  return (
    <div className="item-section">
      <CalculationBox
        label={t('chargingCost.heading', { ns: 'item' })}
        output={
          costPerMonth ? (
            t('Price per month', { ns: 'common', count: costPerMonth })
          ) : (
            <Skeleton $width="115px" $height="18px" $inlineBlock />
          )
        }
        outputIcon={
          <Text as="span" $tone="success">
            <IconLightning />
          </Text>
        }
        disclaimer={t('chargingCost.disclaimer', {
          ns: 'item',
          standardCost: costPerKwh
            ? t('chargingCost.disclaimerCost', {
                ns: 'item',
                cost: numberSeparator(costPerKwh.value * 100),
              })
            : t('chargingCost.disclaimerStandardCostDefault', { ns: 'item' }),
        })}
        action={{
          text: t('chargingCost.showDetailsButton.text', { ns: 'item' }),
          title: t('chargingCost.showDetailsButton.title', { ns: 'item' }),
          onClick: onOpenModal,
        }}
      >
        <StackMedium>
          <H3 as="h2" $margin="small">
            {t('chargingCost.calculateChargingCost', { ns: 'item' })}
          </H3>
          <Content>
            <p>{t('chargingCost.calculateChargingCostDescription', { ns: 'item' })}</p>
          </Content>
        </StackMedium>
        <StackMedium>
          <InputLabel htmlFor="charging-cost-input-mileage">
            {t('chargingCost.yourAnnualMileage', { ns: 'item' })}
          </InputLabel>
          <StackSmall>
            <InputSelect
              id="charging-cost-input-mileage"
              title={t('chargingCost.yourAnnualMileage', { ns: 'item' })}
              value={distance}
              onChange={onDistanceChangeSelect}
              options={DistanceOptions.map((d) => ({
                value: d,
                displayName: prettify(
                  d * mileageMultiplier,
                  t('mileageUnit', { ns: 'glossary', count: 2 }),
                ),
              }))}
            />
          </StackSmall>
          <StackSmall>
            <RangeSliderSimple
              step={500}
              min={500}
              max={5000}
              value={distance}
              onChange={onDistanceUpdate}
              onAfterChange={onDistanceAfterChange}
            />
          </StackSmall>
        </StackMedium>
        <StackMedium>
          <Box $tone="accent">
            <Columns $spacing={2} $valign="center">
              <Column $noShrink>
                <IconBackdrop $tone="success">
                  <IconBulb />
                </IconBackdrop>
              </Column>
              <Column $grow>
                <StackTiny>
                  <Text $weight="medium">
                    {t('chargingCost.adjustAfterYourOwnElectricityPrice', { ns: 'item' })}
                  </Text>
                </StackTiny>
                <StackTiny>
                  <LinkButton
                    onClick={() => onOpenModal(2)}
                    title={t('chargingCost.openCalculator', { ns: 'item' })}
                  >
                    {t('chargingCost.openCalculator', { ns: 'item' })}
                  </LinkButton>
                </StackTiny>
              </Column>
            </Columns>
          </Box>
        </StackMedium>
        {request.isError && (
          <StackMedium>
            <Alert tone="error">{t('chargingCost.couldNotFetchData', { ns: 'item' })}</Alert>
          </StackMedium>
        )}
      </CalculationBox>
      {!!modal && data && (
        <ChargingCostModal
          id={id}
          distanceOptions={DistanceOptions}
          defaultData={data}
          costPerMonth={costPerMonth}
          distance={distance}
          item={item}
          customPriceAsDefault={modal === 2}
          onDistanceUpdate={onDistanceUpdate}
          onDistanceChangeSelect={onDistanceChangeSelect}
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default ChargingCost;
